<template>
  <div>
    <!-- Period filter -->
    <b-card
no-body
class="mb-1"
>
      <div class="m-2">
        <b-row class="align-items-end">
          <b-col
cols="6"
md="3"
>
            <label>From</label>
            <b-form-datepicker v-model="dateFrom" />
          </b-col>
          <b-col
cols="6"
md="3"
>
            <label>To</label>
            <b-form-datepicker v-model="dateTo" />
          </b-col>
          <b-col
cols="12"
md="3"
class="mt-1 mt-md-0"
>
            <b-button
variant="primary"
:disabled="loading"
@click="fetchReport"
>
              <b-spinner
v-if="loading"
small
class="mr-50"
/>
              Apply
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Summary cards -->
    <b-row>
      <b-col
v-for="card in summaryCards"
:key="card.title"
cols="6"
md="4"
lg="2"
>
        <b-card class="text-center">
          <h2
class="mb-25"
:class="card.class"
>
{{ card.value }}
</h2>
          <div class="text-muted small">
{{ card.title }}
</div>
        </b-card>
      </b-col>
    </b-row>

    <!-- 1. Central invoice: amount per currency -->
    <b-card
title="Central Invoice Amount"
no-body
class="p-1"
>
      <b-table
        :items="amountByCurrency"
        :fields="amountFields"
        small
        responsive
        show-empty
        empty-text="No data"
      >
        <template #cell(AmountWithPusat)="data">
{{ money(data.value) }}
</template>
        <template #cell(AmountWithoutPusat)="data">
          <span class="text-danger">{{ money(data.value) }}</span>
        </template>
        <template #cell(AmountTotal)="data">
{{ money(data.value) }}
</template>
      </b-table>
    </b-card>

    <!-- 1-4. Invoice per branch -->
    <b-card
title="Invoice per Branch"
no-body
class="p-1"
>
      <b-table
        :items="invoiceByBranch"
        :fields="invoiceFields"
        small
        responsive
        show-empty
        empty-text="No data"
      >
        <template #cell(Branch)="data">
{{ branchLabel(data.item) }}
</template>
        <template #cell(AvgLeadTimeDays)="data">
          {{ data.value === null || data.value === undefined ? '-' : data.value }}
        </template>
      </b-table>
    </b-card>

    <!-- 6. Jobs per branch -->
    <b-row>
      <b-col
lg="5"
cols="12"
>
        <b-card
title="Jobs per Branch (top 15)"
no-body
class="p-1"
>
          <div
v-if="!jobChartData.labels.length"
class="text-center text-muted py-4"
>
            No data
          </div>
          <chartjs-component-bar-chart
            v-else
            :height="320"
            :data="jobChartData"
            :options="barOptions"
          />
        </b-card>
      </b-col>
      <b-col
lg="7"
cols="12"
>
        <b-card
title="Jobs per Month per Branch"
no-body
class="p-1"
>
          <b-table
            :items="jobPivotRows"
            :fields="jobPivotFields"
            small
            responsive
            show-empty
            empty-text="No data"
            sort-by="Total"
            sort-desc
          />
        </b-card>
      </b-col>
    </b-row>

    <!-- 7. Jobs without invoice -->
    <b-card
no-body
class="p-1"
>
      <h4 class="card-title">
        Jobs without Invoice
        <small class="text-muted">
          ({{ jobSummary.Outstanding }} of {{ jobSummary.Jobs }} jobs)
        </small>
      </h4>
      <b-table
        :items="jobsOutstanding"
        :fields="outstandingFields"
        small
        responsive
        show-empty
        empty-text="No data"
      >
        <template #cell(Branch)="data">
{{ branchLabel(data.item) }}
</template>
      </b-table>
    </b-card>

    <!-- 5. Top customers -->
    <b-card
title="Top Customers"
no-body
class="p-1"
>
      <div
v-if="!currencies.length"
class="text-center text-muted py-2"
>
No data
</div>
      <b-tabs v-else>
        <b-tab
v-for="cur in currencies"
:key="cur"
:title="cur"
>
          <b-table
:items="topCustomers[cur]"
:fields="customerFields"
small
responsive
>
            <template #cell(Amount)="data">
{{ money(data.value) }}
</template>
          </b-table>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BSpinner, BFormDatepicker, BTable, BTabs, BTab,
} from 'bootstrap-vue'
import axios from 'axios'
import moment from 'moment'
import { getToken } from '@/auth/utils'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ChartjsComponentBarChart from '../income/charts-components/ChartjsComponentBarChart.vue'

const REPORT_URL = 'https://api.geolims.com/dashboard_service/reports'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BFormDatepicker,
    BTable,
    BTabs,
    BTab,
    ChartjsComponentBarChart,
  },
  data() {
    return {
      loading: false,
      dateFrom: moment().subtract(2, 'months').startOf('month').format('YYYY-MM-DD'),
      dateTo: moment().format('YYYY-MM-DD'),
      report: null,
      barOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
        },
      },
      amountFields: [
        { key: 'Currency' },
        { key: 'AmountWithPusat', label: 'With Central Invoice', class: 'text-right' },
        { key: 'AmountWithoutPusat', label: 'Without Central Invoice', class: 'text-right' },
        { key: 'AmountTotal', label: 'Total', class: 'text-right' },
      ],
      invoiceFields: [
        { key: 'Branch' },
        { key: 'Total', sortable: true },
        { key: 'WithPusat', label: 'With Pusat', sortable: true },
        { key: 'WithoutPusat', label: 'Without Pusat', sortable: true },
        { key: 'Approved', sortable: true },
        { key: 'NotApproved', label: 'Not Approved', sortable: true },
        { key: 'NotApproved_30_plus', label: 'Not Approved > 30d', sortable: true },
        { key: 'WithoutDoc', label: 'No Supporting Doc', sortable: true },
        { key: 'AvgLeadTimeDays', label: 'Avg Lead Time (days)', sortable: true },
      ],
      outstandingFields: [
        { key: 'Branch' },
        { key: 'Jobs', sortable: true },
        { key: 'Outstanding', sortable: true },
        { key: 'Outstanding_0_30', label: '0-30d' },
        { key: 'Outstanding_31_60', label: '31-60d' },
        { key: 'Outstanding_61_90', label: '61-90d' },
        { key: 'Outstanding_90_plus', label: '> 90d' },
      ],
      customerFields: [
        { key: 'Customer' },
        { key: 'Invoices', class: 'text-right' },
        { key: 'Amount', class: 'text-right' },
      ],
    }
  },
  computed: {
    invoices() {
      return (this.report && this.report.Invoices) || {}
    },
    jobs() {
      return (this.report && this.report.Jobs) || {}
    },
    invoiceByBranch() {
      return this.invoices.ByBranch || []
    },
    amountByCurrency() {
      return this.invoices.AmountByCurrency || []
    },
    topCustomers() {
      return this.invoices.TopCustomers || {}
    },
    currencies() {
      return Object.keys(this.topCustomers)
    },
    jobsOutstanding() {
      return this.jobs.OutstandingByBranch || []
    },
    jobSummary() {
      return this.jobs.Summary || { Jobs: 0, Outstanding: 0 }
    },
    summaryCards() {
      const s = this.invoices.Summary || {}
      const lead = s.AvgLeadTimeDays
      return [
        { title: 'Invoices without Pusat', value: this.num(s.WithoutPusat), class: 'text-danger' },
        { title: 'Invoices with Pusat', value: this.num(s.WithPusat), class: 'text-success' },
        { title: 'Not Approved', value: this.num(s.NotApproved), class: 'text-warning' },
        { title: 'No Supporting Doc', value: this.num(s.WithoutDoc), class: 'text-warning' },
        {
          title: 'Avg Lead Time to Pusat (days)',
          value: lead === null || lead === undefined ? '-' : lead,
          class: '',
        },
        { title: 'Jobs without Invoice', value: this.num(this.jobSummary.Outstanding), class: 'text-danger' },
      ]
    },
    jobsByBranchTotal() {
      const totals = {}
      const monthly = this.jobs.MonthlyByBranch || []
      monthly.forEach(r => {
        const key = r.KodeUnitKerja
        if (!totals[key]) totals[key] = { label: this.branchLabel(r), total: 0 }
        totals[key].total += r.Jobs
      })
      return Object.values(totals).sort((a, b) => b.total - a.total)
    },
    jobChartData() {
      const top = this.jobsByBranchTotal.slice(0, 15)
      return {
        labels: top.map(t => t.label),
        datasets: [{
          label: 'Jobs',
          data: top.map(t => t.total),
          backgroundColor: '#7367f0',
        }],
      }
    },
    jobMonths() {
      const months = new Set()
      const monthly = this.jobs.MonthlyByBranch || []
      monthly.forEach(r => months.add(r.YearMonth))
      return [...months].sort()
    },
    jobPivotRows() {
      const rows = {}
      const monthly = this.jobs.MonthlyByBranch || []
      monthly.forEach(r => {
        if (!rows[r.KodeUnitKerja]) {
          rows[r.KodeUnitKerja] = { Branch: this.branchLabel(r), Total: 0 }
        }
        rows[r.KodeUnitKerja][r.YearMonth] = r.Jobs
        rows[r.KodeUnitKerja].Total += r.Jobs
      })
      return Object.values(rows)
    },
    jobPivotFields() {
      return [
        { key: 'Branch', sortable: true },
        ...this.jobMonths.map(m => ({ key: m, label: m, sortable: true })),
        { key: 'Total', sortable: true },
      ]
    },
  },
  mounted() {
    this.fetchReport()
  },
  methods: {
    num(value) {
      return value === null || value === undefined ? '-' : Number(value).toLocaleString('id-ID')
    },
    money(value) {
      return Number(value || 0).toLocaleString('id-ID', { maximumFractionDigits: 0 })
    },
    branchLabel(item) {
      return item.Branch && item.Branch !== item.KodeUnitKerja
        ? `${item.KodeUnitKerja} - ${item.Branch}`
        : item.KodeUnitKerja
    },
    fetchReport() {
      this.loading = true
      const headers = {
        'Content-Type': 'application/json',
        'X-Token-Access': `Bearer ${getToken()}`,
      }
      axios
        .post(REPORT_URL, { DateFrom: this.dateFrom, DateTo: this.dateTo }, { headers })
        .then(response => {
          this.report = response.data.Payload || null
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error fetching report',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>
